import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { IMG } from "@/lib/brand";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Section";
import { HexWatermark } from "@/components/BrandMark";
import { useLang } from "@/lib/LanguageContext";

const PRODUCT_IMAGES = [IMG.pellets, IMG.texture1, IMG.wood, IMG.polymers1, IMG.polymers2, IMG.printing, IMG.machinery];

export default function Products() {
  const { t } = useLang();
  const p = t.products;

  return (
    <div data-testid="products-page" className="bg-white">
      <PageHero
        testId="products-hero"
        eyebrow={p.hero_eyebrow}
        title={p.hero_title}
        intro={p.hero_intro}
        image={IMG.polymers1}
      />

      <section className="container-x py-24 sm:py-32">
        <div className="space-y-24 sm:space-y-32">
          {p.groups.map((g, idx) => (
            <Reveal key={idx}>
              <article
                data-testid={`product-group-${idx}`}
                className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-7 img-zoom border border-border bg-secondary aspect-[4/3]">
                  <img src={PRODUCT_IMAGES[idx]} alt={g.name} className="w-full h-full object-cover" />
                </div>
                <div className="lg:col-span-5">
                  <div className="flex items-baseline gap-5">
                    <span className="text-[11px] tabular-nums tracking-[0.22em] uppercase text-muted-foreground">
                      {p.group_label} {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight font-medium leading-[1.05]">{g.name}</h2>
                  <div className="mt-3 text-accent font-medium tracking-wide text-sm">{g.tagline}</div>
                  <p className="mt-6 text-muted-foreground leading-relaxed text-lg">{g.description}</p>
                  <ul className="mt-8 grid gap-px bg-border border border-border">
                    {g.specs.map((s) => (
                      <li key={s} className="bg-white px-5 py-3 text-sm text-primary">{s}</li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative bg-primary text-primary-foreground hex-pattern-light overflow-hidden">
        <HexWatermark
          opacity={0.08}
          size={780}
          strokeWidth={1.5}
          className="text-white -left-60 top-1/2 -translate-y-1/2"
        />
        <div className="relative container-x py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <SectionEyebrow light>{p.bottom_eyebrow}</SectionEyebrow>
            <h2 className="mt-5 font-display text-white text-3xl sm:text-5xl tracking-tight font-medium leading-[1.05]">
              {p.bottom_title_l1}
              <br /> {p.bottom_title_l2}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="products-bottom-cta"
              className="group inline-flex items-center gap-3 bg-white text-primary px-7 py-4 text-sm font-medium hover:bg-accent hover:text-white transition-all"
            >
              {p.bottom_cta} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
